import React, { useState, useEffect } from "react";
import axiosInstance from "../../axiosConfig";
import Swal from "sweetalert2";
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import "../../assets/CSS/Dashboard.css";

const IMAGE_BASE_URL = process.env.REACT_APP_API_BASE_PATH || process.env.REACT_APP_API_URL || "https://wip.fibrebondindustries.com/backend";

const WipTicketsDashboard = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);
  const user = JSON.parse(localStorage.getItem("user")) || {};

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/api/wip-workflow-tickets');
      if (res.data.success) {
        setTickets(res.data.tickets);
      }
    } catch (err) {
      console.error("Failed to fetch tickets", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  useEffect(() => {
    setCurrentPage(1); // Reset to page 1 on filter change
  }, [filter, itemsPerPage]);

  const handleResolve = async (ticketId) => {
    const { value: remark } = await Swal.fire({
      title: 'Resolve Ticket',
      input: 'textarea',
      inputLabel: 'Resolution Remark',
      inputPlaceholder: 'Type your resolution details here...',
      inputAttributes: {
        'aria-label': 'Type your resolution details here'
      },
      showCancelButton: true,
      confirmButtonText: 'Resolve',
      confirmButtonColor: '#10b981',
      inputValidator: (value) => {
        if (!value) {
          return 'You need to write something!'
        }
      }
    });

    if (remark) {
      try {
        const res = await axiosInstance.post('/api/wip-workflow-tickets/resolve', {
          TicketID: ticketId,
          ResolvedBy: user.Name,
          ResolutionRemark: remark
        });
        if (res.data.success) {
          Swal.fire('Resolved!', 'Ticket has been marked as resolved.', 'success');
          fetchTickets();
        }
      } catch (err) {
        Swal.fire('Error', 'Failed to resolve ticket', 'error');
      }
    }
  };

  const viewPhotos = (photosStr) => {
    try {
      const photos = JSON.parse(photosStr || '[]');
      if (photos.length === 0) return Swal.fire('No Photos', 'No evidence photos uploaded.', 'info');
      
      let html = '<div style="display:flex;flex-wrap:wrap;gap:10px;justify-content:center;">';
      photos.forEach(p => {
        const fullUrl = p.startsWith('http') ? p : `${IMAGE_BASE_URL}${p}`;
        html += `<a href="${fullUrl}" target="_blank"><img src="${fullUrl}" style="width:120px;height:120px;object-fit:cover;border-radius:8px;border:1px solid #ddd;"/></a>`;
      });
      html += '</div><p style="margin-top:10px;font-size:12px;color:#888;">Click on image to view full size.</p>';
      
      Swal.fire({
        title: 'Evidence Photos',
        html: html,
        width: 600,
        showConfirmButton: false,
        showCloseButton: true
      });
    } catch (e) {
      Swal.fire('Error', 'Could not load photos', 'error');
    }
  };

  const filteredTickets = tickets.filter(t => {
    const statusMatch = filter === 'All' ? true : t.Status === filter;
    const searchMatch = searchQuery === '' ? true : (
      (t.ItemName && t.ItemName.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (t.ProcessName && t.ProcessName.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (t.TicketID && t.TicketID.toString().includes(searchQuery))
    );
    return statusMatch && searchMatch;
  });
  
  // Pagination logic
  const indexOfLastItem = itemsPerPage === 'All' ? filteredTickets.length : currentPage * itemsPerPage;
  const indexOfFirstItem = itemsPerPage === 'All' ? 0 : indexOfLastItem - itemsPerPage;
  const paginatedTickets = filteredTickets.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = itemsPerPage === 'All' ? 1 : Math.ceil(filteredTickets.length / itemsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  return (
    <div className="d-flex dashboard">
      <div className={isSidebarVisible ? "sidebar-container shadow-sm" : "sidebar-hidden"}><Sidebar /></div>

      <div className="flex-grow-1">
        <Header toggleSidebar={toggleSidebar} isSidebarVisible={isSidebarVisible} />

        <main className="main-container p-0 pb-4" style={{ backgroundColor: "#f8fafc", minHeight: "calc(100vh - 60px)" }}>
          <div style={{ padding: "40px", maxWidth: "1400px", margin: "0 auto" }}>
        
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "30px", flexWrap: "wrap", gap: "20px" }}>
          <div>
            <h2 style={{ fontWeight: "800", color: "#0f172a", margin: 0, display: "flex", alignItems: "center", gap: "10px" }}>
              <i className="bi bi-ticket-detailed-fill text-danger"></i> Lot Workflow Escalations
            </h2>
            <p style={{ color: "#64748b", margin: "5px 0 0 0", fontSize: "15px" }}>Track and resolve lot workflow SLA breaches and issues.</p>
          </div>
          
          <div style={{ display: "flex", gap: "15px", alignItems: "center", flexWrap: "wrap" }}>
            <div className="btn-group shadow-sm" role="group">
              <button type="button" className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-light border'}`} onClick={() => setViewMode('grid')}>
                <i className="bi bi-grid-fill"></i>
              </button>
              <button type="button" className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-light border'}`} onClick={() => setViewMode('list')}>
                <i className="bi bi-list-task"></i>
              </button>
            </div>
            
            <div style={{ display: "flex", alignItems: "center", gap: "10px", background: "#fff", border: "1px solid #cbd5e1", borderRadius: "10px", padding: "4px 12px" }}>
              <span style={{ fontSize: "14px", fontWeight: "600", color: "#475569" }}>Show:</span>
              <select className="form-select border-0 bg-transparent shadow-none" style={{ width: "auto", cursor: "pointer", fontWeight: "bold" }} value={itemsPerPage} onChange={e => setItemsPerPage(e.target.value === 'All' ? 'All' : Number(e.target.value))}>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={30}>30</option>
                <option value="All">All</option>
              </select>
            </div>

            <input 
              type="text" 
              className="form-control shadow-sm" 
              placeholder="Search ID, Item or Process..." 
              style={{ width: "220px", borderRadius: "10px", border: "1px solid #cbd5e1" }}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />

            <select className="form-select shadow-sm" style={{ width: "160px", borderRadius: "10px", border: "1px solid #cbd5e1", fontWeight: "600" }} value={filter} onChange={e => setFilter(e.target.value)}>
              <option value="All">All Status</option>
              <option value="Open">Open</option>
              <option value="Resolved">Resolved</option>
            </select>

            <button className="btn btn-light border shadow-sm fw-bold" style={{ borderRadius: "10px" }} onClick={fetchTickets}>
              <i className="bi bi-arrow-clockwise"></i> Refresh
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "60px", color: "#64748b" }}><i className="bi bi-arrow-repeat spin" style={{ fontSize: "30px" }}></i></div>
        ) : filteredTickets.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px", background: "#fff", borderRadius: "16px", boxShadow: "0 10px 15px -3px rgba(0,0,0,0.05)", border: "1px solid #e2e8f0" }}>
            <i className="bi bi-check-circle text-success" style={{ fontSize: "40px" }}></i>
            <h4 style={{ marginTop: "16px", color: "#475569" }}>All clear!</h4>
            <p className="text-muted">No tickets found for the selected filter.</p>
          </div>
        ) : (
          <>
          {viewMode === 'grid' ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(400px, 1fr))", gap: "20px" }}>
              {paginatedTickets.map(ticket => (
                <div key={ticket.TicketID} style={{ background: "#fff", borderRadius: "16px", padding: "24px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)", border: "1px solid #e2e8f0", position: "relative", display: "flex", flexDirection: "column" }}>
                  
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
                    <div>
                      <span style={{ fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>TICKET #{ticket.TicketID}</span>
                      <h5 style={{ margin: "4px 0 0 0", fontWeight: "800", color: "#1e293b", fontSize: "18px" }}>{ticket.LOT_ID}</h5>
                    </div>
                    <span style={{ padding: "6px 12px", borderRadius: "20px", fontSize: "12px", fontWeight: "700", backgroundColor: ticket.Status === 'Resolved' ? '#f0fdf4' : ticket.Status === 'Escalated' ? '#fef2f2' : '#fffbeb', color: ticket.Status === 'Resolved' ? '#16a34a' : ticket.Status === 'Escalated' ? '#ef4444' : '#d97706', border: `1px solid ${ticket.Status === 'Resolved' ? '#bbf7d0' : ticket.Status === 'Escalated' ? '#fecaca' : '#fde68a'}` }}>
                      {ticket.Status}
                    </span>
                  </div>

                  <div style={{ background: "#f8fafc", borderRadius: "8px", padding: "16px", marginBottom: "16px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
                      <span className="text-muted small fw-bold">Process:</span>
                      <span className="fw-bold text-dark small">{ticket.ProcessName}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
                      <span className="text-muted small fw-bold">Reported By:</span>
                      <span className="fw-bold text-dark small">{ticket.ReporterName || ticket.ReportedBy}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
                      <span className="text-muted small fw-bold">Reported At:</span>
                      <span className="fw-bold text-dark small">
                        {ticket.ReportedAt ? new Date(ticket.ReportedAt).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: 'medium', timeStyle: 'short' }) : 'N/A'}
                      </span>
                    </div>
                    {ticket.ResolvedBy && (
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
                        <span className="text-muted small fw-bold">Updated By:</span>
                        <span className="fw-bold text-dark small">{ticket.ResolverName || ticket.ResolvedBy}</span>
                      </div>
                    )}
                    {ticket.ResolvedAt && (
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
                        <span className="text-muted small fw-bold">Updated At:</span>
                        <span className="fw-bold text-dark small">
                          {new Date(ticket.ResolvedAt).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: 'medium', timeStyle: 'short' })}
                        </span>
                      </div>
                    )}
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px", marginTop: "10px", paddingTop: "10px", borderTop: "1px dashed #cbd5e1" }}>
                      <span className="text-muted small fw-bold">SLA Deadline:</span>
                      <span className={`fw-bold small ${ticket.IsEscalated ? 'text-danger' : 'text-dark'}`}>
                        {ticket.SLA_Deadline ? new Date(ticket.SLA_Deadline).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: 'medium', timeStyle: 'short' }) : 'N/A'}
                      </span>
                    </div>
                    {ticket.EscalatedTo && (
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span className="text-muted small fw-bold">Escalated To:</span>
                        <span className="fw-bold text-danger small">{ticket.EscalatedTo}</span>
                      </div>
                    )}
                  </div>

                  {ticket.IssueDetail && (
                    <div style={{ marginBottom: "16px", fontSize: "14px", color: "#475569" }}>
                      <strong>Remark: </strong> {ticket.IssueDetail}
                    </div>
                  )}

                  <div style={{ marginTop: "auto", display: "flex", gap: "10px", borderTop: "1px solid #f1f5f9", paddingTop: "16px" }}>
                    <button onClick={() => viewPhotos(ticket.EvidencePhotos)} className="btn btn-light btn-sm fw-bold flex-grow-1" style={{ border: "1px solid #cbd5e1" }}>
                      <i className="bi bi-images me-1"></i> View Photos
                    </button>
                    {ticket.Status === 'Open' ? (
                      <button onClick={() => handleResolve(ticket.TicketID)} className="btn btn-success btn-sm fw-bold flex-grow-1">
                        <i className="bi bi-check-circle me-1"></i> Resolve
                      </button>
                    ) : (
                      <div className="flex-grow-1 text-end align-self-center">
                        <span className="small text-success fw-bold"><i className="bi bi-check-all"></i> Resolved by {ticket.ResolverName || ticket.ResolvedBy}</span>
                      </div>
                    )}
                  </div>

                  {ticket.Status === 'Resolved' && ticket.ResolutionRemark && (
                    <div style={{ marginTop: "12px", background: "#f0fdf4", padding: "10px", borderRadius: "6px", fontSize: "13px", color: "#16a34a", border: "1px solid #bbf7d0" }}>
                      <strong>Admin Note:</strong> {ticket.ResolutionRemark}
                    </div>
                  )}
                  
                </div>
              ))}
            </div>
          ) : (
            <div className="table-responsive shadow-sm" style={{ background: "#fff", borderRadius: "16px", border: "1px solid #e2e8f0" }}>
              <table className="table table-hover align-middle mb-0" style={{ fontSize: "14px" }}>
                <thead style={{ background: "#1e293b", color: "#ffffff" }}>
                  <tr>
                    <th className="px-4 py-3 text-white text-uppercase" style={{ fontSize: "12px", letterSpacing: "0.5px" }}>ID / Lot</th>
                    <th className="px-4 py-3 text-white text-uppercase" style={{ fontSize: "12px", letterSpacing: "0.5px" }}>Process</th>
                    <th className="px-4 py-3 text-white text-uppercase" style={{ fontSize: "12px", letterSpacing: "0.5px" }}>Reported By & At</th>
                    <th className="px-4 py-3 text-white text-uppercase" style={{ fontSize: "12px", letterSpacing: "0.5px" }}>SLA & Escalation</th>
                    <th className="px-4 py-3 text-white text-uppercase" style={{ fontSize: "12px", letterSpacing: "0.5px" }}>Status</th>
                    <th className="px-4 py-3 text-white text-uppercase text-end" style={{ fontSize: "12px", letterSpacing: "0.5px" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTickets.map(ticket => (
                    <tr key={ticket.TicketID}>
                      <td className="px-4 py-3">
                        <div className="fw-bold text-dark">{ticket.LOT_ID}</div>
                        <div className="text-muted small">#{ticket.TicketID}</div>
                      </td>
                      <td className="px-4 py-3 fw-bold text-secondary">{ticket.ProcessName}</td>
                      <td className="px-4 py-3">
                        <div className="fw-bold text-dark">{ticket.ReporterName || ticket.ReportedBy}</div>
                        <div className="text-muted small">
                          {ticket.IssueRaisedAt ? new Date(ticket.IssueRaisedAt).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: 'medium', timeStyle: 'short' }) : 'N/A'}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className={`fw-bold small ${ticket.IsEscalated ? 'text-danger' : 'text-dark'}`}>
                          <i className="bi bi-clock-history me-1"></i>
                          {ticket.SLA_Deadline ? new Date(ticket.SLA_Deadline).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: 'medium', timeStyle: 'short' }) : 'N/A'}
                        </div>
                        {ticket.EscalatedTo && (
                          <div className="text-danger small fw-bold mt-1">
                            <i className="bi bi-exclamation-triangle-fill me-1"></i>To: {ticket.EscalatedTo}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span style={{ padding: "4px 10px", borderRadius: "12px", fontSize: "11px", fontWeight: "700", backgroundColor: ticket.Status === 'Resolved' ? '#f0fdf4' : ticket.Status === 'Escalated' ? '#fef2f2' : '#fffbeb', color: ticket.Status === 'Resolved' ? '#16a34a' : ticket.Status === 'Escalated' ? '#ef4444' : '#d97706', border: `1px solid ${ticket.Status === 'Resolved' ? '#bbf7d0' : ticket.Status === 'Escalated' ? '#fecaca' : '#fde68a'}` }}>
                          {ticket.Status}
                        </span>
                        {ticket.Status === 'Resolved' && (
                          <div className="text-muted small mt-1" style={{ fontSize: "11px" }}>
                            by {ticket.ResolverName || ticket.ResolvedBy}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-end">
                        <button onClick={() => viewPhotos(ticket.EvidencePhotos)} className="btn btn-light btn-sm fw-bold border me-2" title="View Photos">
                          <i className="bi bi-images"></i>
                        </button>
                        {ticket.Status !== 'Resolved' && (
                          <button onClick={() => handleResolve(ticket.TicketID)} className="btn btn-success btn-sm fw-bold" title="Resolve">
                            <i className="bi bi-check-circle"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {itemsPerPage !== 'All' && totalPages > 1 && (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "15px", marginTop: "30px" }}>
              <button 
                className="btn btn-light border shadow-sm fw-bold" 
                onClick={() => handlePageChange(currentPage - 1)} 
                disabled={currentPage === 1}
                style={{ borderRadius: "8px", padding: "6px 16px" }}
              >
                <i className="bi bi-chevron-left me-1"></i> Prev
              </button>
              
              <span style={{ fontWeight: "700", color: "#475569" }}>
                Page {currentPage} of {totalPages}
              </span>
              
              <button 
                className="btn btn-light border shadow-sm fw-bold" 
                onClick={() => handlePageChange(currentPage + 1)} 
                disabled={currentPage === totalPages}
                style={{ borderRadius: "8px", padding: "6px 16px" }}
              >
                Next <i className="bi bi-chevron-right ms-1"></i>
              </button>
            </div>
          )}
          </>
        )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default WipTicketsDashboard;
